import { create, el, resizer } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents } from "@dhx/ts-data";

import { ComposeLayer } from "./ComposeLayer";
import { Legend } from "./Legend";

import scaleTypes from "./scales/index";
import chartTypes from "./series/index";


import Stacker from "./series/Stacker";

import { Tooltip } from "./Tooltip";

import { ChartEvents, IChart, IChartConfig, IChartSeries, IComposeLayer, IScaleConfig, IScales, ISeria, ScaleType } from "./types";


export class Chart extends View implements IChart {
	public data: DataCollection;
	public events: IEventSystem<DataEvents | ChartEvents>;
	public config: IChartConfig;

	private _layers: IComposeLayer;
	private _series: IChartSeries;
	private _scales: IScales;
	private _tooltip: Tooltip;

	private _globalHTMLHandlers: any;

	// using zero values ensure that widget will not attempt to render self in the hidden state
	private _width: number = 0;
	private _height: number = 0;

	private _left: number = 0;
	private _top: number = 0;

	constructor(node: HTMLElement | string, config: IChartConfig = {}) {
		super(null, config);

		const dataConfig:any = {};
		if (config.maxPoints){
			dataConfig.approximate = {
				value: config.series.map(a => a.value),
				maxNum: config.maxPoints
			};
		}

		if (Array.isArray(config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection(dataConfig, this.events);
			this.data.parse(config.data);
		} else if (config.data && config.data.events) {
			this.data = config.data;
			this.events = this.data.events as IEventSystem<DataEvents | ChartEvents>;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new DataCollection(dataConfig, this.events);
		}

		this._globalHTMLHandlers = {
			onmousemove: e => {
				const {left, top, bottom, right} = this._layers.getSizes();
				const {pageX, pageY} = e;
				const rects = this.getRootView().node.el.getBoundingClientRect();
				this._left = rects.left + window.pageXOffset;
				this._top = rects.top + window.pageYOffset;

				const x = pageX - left - this._left;
				const y = pageY - top - this._top;
				if (x >= 0 && x <= this._width - right - left && y >= 0 && y <= this._height - bottom - top) {
					this.events.fire(ChartEvents.chartMouseMove, [x, y, this._left + left, this._top + top]);
				} else {
					this.events.fire(ChartEvents.chartMouseLeave);
				}
			},
			onmouseleave: () => this.events.fire(ChartEvents.chartMouseLeave)
		};

		this._layers = new ComposeLayer();
		this.setConfig(config);
		this._initEvents();

		const render = (vm, obj: any) => {
			if (!this.data.getLength()){
				return el("div");
			}

			const content = [
				resizer((x, y) => {
					this._width = x;
					this._height = y || 400; // if height is not provided, use default value

					const view = this.getRootView();
					if (view && view.node && view.node.el) {
						const rects = view.node.el.getBoundingClientRect();
						this._left = rects.left + window.pageXOffset;
						this._top = rects.top + window.pageYOffset;
					}
					this.events.fire(ChartEvents.resize, [{
						width: this._width,
						height: this._height
					}]);
					this.paint();
				})
			];

			if (this._width && this._height){
				content.push(this._layers.toVDOM(this._width, this._height));
			}

			return el(".dhx_widget.dhx_chart", {
				class: config.css ? config.css : "",
				onmousemove: this._globalHTMLHandlers.onmousemove,
				onmouseleave: this._globalHTMLHandlers.onmouseleave
			}, content);
		};
		this.mount(node, create({ render }));
	}
	getSeries(key:string):ISeria{
		return this._series[key];
	}
	eachSeries(handler: (serie: ISeria) => any): any[] {
		const result = [];
		for (const key in this._series){
			result.push(handler.call(this, this._series[key]));
		}
		return result;
	}
	destructor() {
		this._tooltip.destructor();
		this.events.clear();
		this.unmount();
	}
	setConfig(config: IChartConfig): void {
		this.config = config;
		this._layers.clear();
		this._series = {};
		this._scales = {};
		let min;
		// let baseLine;

		if (config.scales) {
			for (const key in config.scales) {
				const scale = {...config.scales[key]};
				if (config.scales[key].min !== undefined) {
					min = config.scales[key].min;
				}
				scale.type = scale.type || this._detectScaleType(scale, key);
				if (config.scales.radial && key !== "radial") {
					scale.hidden = true;
				}
				this._setScale(scale, key);
			}
		}

		const stack = new Stacker();
		this._layers.add(stack);

		config.series.forEach(cfg => {
			if(cfg.baseLine !== undefined && cfg.baseLine < min) {
				cfg.baseLine = undefined;
			}
			const serieConfig = {...cfg};
			serieConfig.type = serieConfig.type || config.type;

			const chartFactory = chartTypes[serieConfig.type];
			if (serieConfig.barWidth || this.config.barWidth) {
				serieConfig.barWidth = serieConfig.barWidth || this.config.barWidth;
			}
			const chart = new chartFactory(this.data, serieConfig, this.events);

			const chartScales = serieConfig.scales || [ ScaleType.bottom, ScaleType.left ];
			chartScales.forEach(type => {
				const scale = this._scales[type];
				if (!scale){ return; }

				chart.addScale(type, scale);
				if (!serieConfig.stacked){
					scale.add(chart);
				} else {
					scale.add(stack);
				}
			});
			this._series[chart.id] = chart;

			if (serieConfig.stacked){
				stack.add(chart);
			} else {
				this._layers.add(chart);
			}
		});

		if (config.legend) {
			const legendConfig = {...config.legend};
			if (legendConfig.series) {
				legendConfig.$seriesInfo = legendConfig.series.map(id => this._series[id]);
			}

			const legend = new Legend(this.data, legendConfig, this.events);
			this._layers.add(legend);
		}

		this._tooltip = new Tooltip(document.body, {chart: this});
		this.paint();
	}

	private _setScale(config: IScaleConfig, position: string): void {
		const scale = new scaleTypes[config.type](this.data, config, position);

		if (scale.config.grid && scale.config.type !== "radial"){
			this._layers.add(scale.scaleGrid());
		}
		this._layers.add(scale);

		this._scales[position] = scale;
	}
	private _detectScaleType(config, key){
		if (key === "radial"){
			return key;
		}
		if (config.text){
			return "text";
		}
		return "numeric";
	}
	private _initEvents(): void {
		// hide/show series on legend click
		this.events.on(ChartEvents.toggleSeries, (id, pieLike) => {
			if (pieLike) {
				const serie = this._series[Object.keys(this._series)[0]];
				if (serie) {
					serie.toggle(id);
					this.paint();
				}
			} else if (this._series[id]) {
				this._series[id].toggle();
				this.paint();
			}
		}, this);

		// repaint on data change
		this.events.on(DataEvents.change, () => this.paint());
	}
}

